import React, { useState, useEffect } from 'react';
import { getPollinationsProfile } from '@/services/pollinations';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { User, Mail, Calendar, CreditCard, ShieldCheck, AlertCircle, RotateCcw } from 'lucide-react';

const PollinationsProfile = () => {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      const { data, error } = await getPollinationsProfile();
      if (error) setError(error);
      else setProfile(data);
      setLoading(false);
    };
    fetchProfile();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48 bg-white/5" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-[300px] rounded-2xl bg-white/5" />
          <Skeleton className="h-[300px] rounded-2xl bg-white/5" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-12 text-center space-y-4">
        <div className="h-16 w-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto">
          <AlertCircle className="text-red-500" size={32} />
        </div>
        <h3 className="text-xl font-syne font-bold text-red-500">{error}</h3>
        <p className="text-sonas-muted text-sm">Verify your system credentials in the secrets manager.</p>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      <section>
        <h1 className="text-2xl font-syne font-bold">Inference Provider Profile</h1>
        <p className="text-sonas-muted text-xs mt-1">Live account status from gen.pollinations.ai</p>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-1 bg-sonas-surface border-sonas-border rounded-2xl overflow-hidden shadow-sm">
          <CardContent className="pt-8 flex flex-col items-center">
            <Avatar className="h-24 w-24 border-2 border-sonas-accent mb-4">
              <AvatarImage src={profile.image} />
              <AvatarFallback className="bg-sonas-surface2 text-2xl font-bold">
                {profile.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <h2 className="text-xl font-syne font-bold">{profile.name}</h2>
            <Badge className="mt-2 bg-sonas-accent text-sonas-bg font-bold uppercase tracking-widest text-[10px]">
              {profile.displayTier || profile.tier}
            </Badge>
          </CardContent>
        </Card>

        <Card className="md:col-span-2 bg-sonas-surface border-sonas-border rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm font-bold uppercase tracking-widest text-sonas-dim">Account Metadata</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-sonas-dim uppercase flex items-center gap-2">
                  <Mail size={12} /> Email Address
                </p>
                <p className="text-sm font-medium">{profile.email}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-sonas-dim uppercase flex items-center gap-2">
                  <ShieldCheck size={12} /> Account Tier
                </p>
                <p className="text-sm font-medium">{profile.tier}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-sonas-dim uppercase flex items-center gap-2">
                  <Calendar size={12} /> Created At
                </p>
                <p className="text-sm font-medium">{profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'N/A'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-sonas-dim uppercase flex items-center gap-2">
                  <RotateCcw size={12} /> Next Reset
                </p>
                <p className="text-sm font-medium">{profile.nextResetDate ? new Date(profile.nextResetDate).toLocaleString() : 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PollinationsProfile;